import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DashboardWidgetInstance, DashboardWidgetRegistration } from './models/dashboard-widget.types';
import { DashboardWidgetRegistryService } from './registry/dashboard-widget-registry.service';

interface DashboardDropPosition {
  x: number;
  y: number;
}

@Component({
  selector: 'jbm-dashboard',
  templateUrl: './jbm-dashboard.component.html',
})
export class JBMDashboardComponent implements OnInit {
  @Input() layout: DashboardWidgetInstance[] | null = null;
  @Input() userRights: any = {};
  @Input() widgetVisibility: Record<string, boolean> = {};
  @Output() onSaveLayout = new EventEmitter<DashboardWidgetInstance[]>();
  readonly gridColumns = 24;
  widgets: DashboardWidgetInstance[] = [];
  selectedType = '';
  draggedWidgetId: string | null = null;
  dropPosition: DashboardDropPosition | null = null;
  private dragStartPosition: DashboardDropPosition | null = null;
  private dragOffset: DashboardDropPosition | null = null;

  constructor(private readonly registry: DashboardWidgetRegistryService) {}

  get availableWidgets(): DashboardWidgetRegistration[] {
    return this.registry.getAll().filter(registration => this.isWidgetVisible(registration.type));
  }

  ngOnInit(): void {
    this.selectedType = this.availableWidgets[0]?.type ?? '';
    const savedLayout = this.layout;
    this.widgets = (savedLayout ?? this.createDefaultLayout())
      .filter(widget => this.isWidgetVisible(widget.type))
      .map(widget => ({
        ...widget,
        closeable: widget.closeable ?? this.registry.get(widget.type)?.closeable ?? true,
        userRights: this.userRights?.[widget.type]
      }));
    if(!this.widgets.length)
      this.widgets=this.createDefaultLayout().slice(0, 1);
    if (!savedLayout) {
      this.reflowLayout();
    }
  }

  public addWidget(type = this.selectedType): void {
    const registration = this.registry.get(type);
    if (!registration) {
      return;
    }

    const position = this.findFirstAvailablePosition(registration.width, this.widgets);
    this.widgets.push({
      id: this.createId(),
      type: registration.type,
      x: position.x,
      y: position.y,
      width: registration.width,
      height: registration.height,
      closeable: registration.closeable,
      userRights: this.userRights?.[registration.type],
      settings: { ...(registration.defaultSettings ?? {}) }
    });
    this.saveLayout();
  }

  removeWidget(widget: DashboardWidgetInstance): void {
    if(!this.canRemoveWidget(widget))
      return;

    this.widgets = this.widgets.filter(item => item.id !== widget.id);
    this.compactEmptyRows();
    this.saveLayout();
  }

  canRemoveWidget(widget: DashboardWidgetInstance): boolean {
    return widget.closeable && this.widgets.length>1;
  }

  moveLeft(index: number): void {
    const widget = this.widgets[index];
    if(!widget || !this.shiftWidgetHorizontally(widget, -1)) {
      return;
    }
    this.saveLayout();
  }

  moveRight(index: number): void {
    const widget = this.widgets[index];
    if(!widget || !this.shiftWidgetHorizontally(widget, 1)) {
      return;
    }
    this.saveLayout();
  }

  canMoveHorizontally(widget: DashboardWidgetInstance, direction: -1 | 1): boolean {
    return this.canPlace(widget, widget.x + direction, widget.y)
      || this.findSwapTarget(widget, direction) !== null
      || this.canPushWidgetHorizontally(widget, direction);
  }

  startDragging(event: DragEvent, widget: DashboardWidgetInstance): void {
    this.draggedWidgetId = widget.id;
    this.dragStartPosition = { x: widget.x, y: widget.y };
    this.dropPosition = null;
    this.dragOffset = null;

    const sourceElement = (event.target as HTMLElement | null)?.closest('.grid-item');
    if (sourceElement && event.clientX > 0 && event.clientY > 0) {
      const sourceRect = sourceElement.getBoundingClientRect();
      this.dragOffset = {
        x: event.clientX - sourceRect.left,
        y: event.clientY - sourceRect.top
      };
    }

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', widget.id);
    }
  }

  dragOverGrid(event: DragEvent): void {
    if (!this.draggedWidgetId) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    const draggedWidget = this.draggedWidget;
    if (!draggedWidget) {
      this.dropPosition = null;
      return;
    }
    const hoveredWidget = this.getWidgetAtEventTarget(event, draggedWidget.id);
    const position = hoveredWidget
      ? { x: hoveredWidget.x, y: hoveredWidget.y }
      : this.getDropPosition(event, event.currentTarget as HTMLElement);
    this.dropPosition = position ? this.resolveDropPosition(draggedWidget, position) : null;
  }

  dragLeaveGrid(event: DragEvent): void {
    const currentTarget = event.currentTarget as HTMLElement | null;
    const relatedTarget = event.relatedTarget as Node | null;

    if (currentTarget && relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }

    this.dropPosition = null;
  }

  dropWidgetAtPointer(event: DragEvent): void {
    event.preventDefault();

    const grid = event.currentTarget as HTMLElement;
    const draggedId = event.dataTransfer?.getData('text/plain') || this.draggedWidgetId;
    const widget = this.widgets.find(item => item.id === draggedId);
    const hoveredWidget = this.getWidgetAtEventTarget(event, draggedId);
    const position = hoveredWidget
      ? { x: hoveredWidget.x, y: hoveredWidget.y }
      : this.dropPosition ?? this.getDropPosition(event, grid);

    if (!widget || !position) {
      this.clearDragState();
      return;
    }

    if (hoveredWidget && hoveredWidget.y !== widget.y && this.swapVerticalWidgetPositions(widget, hoveredWidget)) {
      this.saveLayout();
      this.clearDragState();
      return;
    }

    if (this.tryPlaceWidget(widget, position)) {
      this.saveLayout();
    } else {
      this.reorderWidget(widget.id, this.getDropIndex(event, grid));
    }
    this.clearDragState();
  }

  endDragging(): void {
    const draggingId = this.draggedWidgetId;
    if (!draggingId) {
      return;
    }

    // In sommige browsers wordt dragend vlak voor drop afgevuurd. Geef drop
    // daarom eerst de kans om de laatst berekende previewpositie te gebruiken.
    window.setTimeout(() => {
      if (this.draggedWidgetId === draggingId) {
        this.clearDragState();
      }
    }, 0);
  }

  public updateAllWidgetSettings(settings: Record<string, unknown>): void {
    this.widgets = this.widgets.map(widget => ({
      ...widget,
      settings: { ...widget.settings, ...settings }
    }));
    this.saveLayout();
  }

  public updateAllWidgetContext(context: Record<string, unknown>): void {
    this.widgets = this.widgets.map(widget => ({
      ...widget,
      context: { ...widget.context, ...context }
    }));
  }

  public resetLayout(): void {
    this.widgets = this.createDefaultLayout();
    this.reflowLayout();
    this.saveLayout();
  }

  widgetChanged(widget: DashboardWidgetInstance): void {
    if (!this.canPlace(widget, widget.x, widget.y)) {
      const position = this.findFirstAvailablePosition(
        widget.width,
        this.widgets.filter(item => item.id !== widget.id)
      );
      widget.x = position.x;
      widget.y = position.y;
    }
    this.saveLayout();
  }

  getGridColumn(widget: DashboardWidgetInstance): string {
    const x = Math.max(0, Math.min(this.gridColumns - 1, widget.x || 0));
    return `${x + 1} / span ${this.normalizeWidth(widget.width)}`;
  }

  getGridRow(widget: DashboardWidgetInstance): string {
    const y = Math.max(0, widget.y || 0);
    const height = Math.max(1, widget.height || 1);
    return `${y + 1} / span ${height}`;
  }

  get dropGridColumn(): string {
    const widget = this.draggedWidget;
    if (!widget || !this.dropPosition) {
      return '1 / span 1';
    }
    return `${this.dropPosition.x + 1} / span ${this.normalizeWidth(widget.width)}`;
  }

  get dropGridRow(): string | null {
    const widget = this.draggedWidget;
    if(!this.dropPosition || !widget) {
      return null;
    }

    const height = Math.max(1, widget.height || 1);
    return `${this.dropPosition.y + 1} / span ${height}`;
  }

  get showDropPreview(): boolean {
    const widget = this.draggedWidget;
    const startPosition = this.dragStartPosition;
    const position = this.dropPosition;
    if (!widget || !startPosition || !position) {
      return false;
    }

    return position.x !== startPosition.x || position.y !== startPosition.y;
  }

  trackByWidgetId(_index: number, widget: DashboardWidgetInstance): string {
    return widget.id;
  }

  saveLayout(): void {
    const layout = this.widgets.map(widget => {
      const { context, ...layoutWidget } = widget;
      return {
        ...layoutWidget,
        settings: { ...widget.settings }
      };
    });
    this.onSaveLayout.emit(layout);

  }

  private clearDragState(): void {
    this.draggedWidgetId = null;
    this.dropPosition = null;
    this.dragStartPosition = null;
    this.dragOffset = null;
  }

  private get draggedWidget(): DashboardWidgetInstance | null {
    return this.widgets.find(widget => widget.id === this.draggedWidgetId) ?? null;
  }

  private isWidgetVisible(widgetType: string): boolean {
    if (this.widgetVisibility[widgetType] !== undefined) {
      return this.widgetVisibility[widgetType];
    }

    return this.registry.get(widgetType)?.visible !== false;
  }

  private getWidgetAtEventTarget(event: DragEvent, excludedId: string | null): DashboardWidgetInstance | null {
    const target = event.target as HTMLElement | null;
    const widgetId = target?.closest('.grid-item')?.getAttribute('data-widget-id');
    if (!widgetId || widgetId === excludedId) {
      return null;
    }
    return this.widgets.find(widget => widget.id === widgetId) ?? null;
  }

  private swapVerticalWidgetPositions(
    widget: DashboardWidgetInstance,
    target: DashboardWidgetInstance
  ): boolean {
    const sourceY = widget.y;
    const targetY = target.y;
    const others = this.widgets.filter(item => item.id !== widget.id && item.id !== target.id);
    const widgetAtTarget = { ...widget, y: targetY };
    const targetAtSource = { ...target, y: sourceY };

    if (!this.canFit(this.normalizeWidth(widget.width), widget.x, targetY, [...others, targetAtSource])
      || !this.canFit(this.normalizeWidth(target.width), target.x, sourceY, [...others, widgetAtTarget])) {
      return false;
    }

    widget.y = targetY;
    target.y = sourceY;
    return true;
  }

  private touchesWidget(
    draggedWidget: DashboardWidgetInstance,
    position: DashboardDropPosition
  ): boolean {
    const width = this.normalizeWidth(draggedWidget.width);
    return this.widgets.some(widget =>
      widget.id !== draggedWidget.id
      && widget.y === position.y
      && position.x <= widget.x + this.normalizeWidth(widget.width)
      && position.x + width >= widget.x
    );
  }

  private getDropIndex(event: DragEvent, grid: HTMLElement): number {
    const items = Array.from(grid.querySelectorAll<HTMLElement>('.grid-item'))
      .map((element, index) => ({ index, rect: element.getBoundingClientRect() }));
    const rows: Array<{ top: number; bottom: number; items: typeof items }> = [];

    items.forEach(item => {
      const row = rows[rows.length - 1];
      if (!row || Math.abs(row.top - item.rect.top) > 2) {
        rows.push({ top: item.rect.top, bottom: item.rect.bottom, items: [item] });
      } else {
        row.bottom = Math.max(row.bottom, item.rect.bottom);
        row.items.push(item);
      }
    });

    const row = rows.find(candidate => event.clientY >= candidate.top && event.clientY <= candidate.bottom);
    if (!row) {
      return rows.find(candidate => event.clientY < candidate.top)?.items[0].index ?? this.widgets.length;
    }

    const rowItems = [...row.items].sort((left, right) => left.rect.left - right.rect.left);
    const hoveredItem = rowItems.find(item => event.clientX >= item.rect.left && event.clientX <= item.rect.right);
    if (hoveredItem) {
      return event.clientY < hoveredItem.rect.top + hoveredItem.rect.height / 2
        ? hoveredItem.index
        : hoveredItem.index + 1;
    }

    return rowItems.find(item => event.clientX < item.rect.left)?.index
      ?? rowItems[rowItems.length - 1].index + 1;
  }

  private getDropPosition(event: DragEvent, grid: HTMLElement): DashboardDropPosition | null {
    const widget = this.draggedWidget;
    if (!widget) {
      return null;
    }

    const styles = getComputedStyle(grid);
    const gap = this.parsePixels(styles.columnGap, 16);
    const columnWidth = (grid.clientWidth - gap * (this.gridColumns - 1)) / this.gridColumns;
    if (columnWidth <= 0) {
      return null;
    }

    const gridRect = grid.getBoundingClientRect();
    const hasPointerX = event.clientX > 0;
    const hasPointerY = event.clientY > 0;
    const pointerX = event.clientX - (this.dragOffset?.x ?? 0);
    const pointerY = event.clientY - (this.dragOffset?.y ?? 0);
    const rawX = hasPointerX
      ? Math.floor((pointerX - gridRect.left + gap / 2) / (columnWidth + gap))
      : this.dropPosition?.x ?? this.dragStartPosition?.x ?? widget.x;
    const maxX = this.gridColumns - this.normalizeWidth(widget.width);
    const x = Math.max(0, Math.min(maxX, rawX));

    const y = hasPointerY
      ? this.getDropRow(pointerY, grid, gap)
      : this.dropPosition?.y ?? this.dragStartPosition?.y ?? widget.y;
    return { x, y };
  }

  private getDropRow(clientY: number, grid: HTMLElement, gap: number): number {
    const gridRect = grid.getBoundingClientRect();
    const rowHeights = getComputedStyle(grid).gridTemplateRows
      .split(' ')
      .map(value => parseFloat(value))
      .filter(value => value > 0);

    if (rowHeights.length === 0) {
      return Math.max(0, Math.floor((clientY - gridRect.top) / (220 + gap)));
    }

    let offset = 0;
    for (let row = 0; row < rowHeights.length; row += 1) {
      const rowBottom = gridRect.top + offset + rowHeights[row];
      if (clientY <= rowBottom + gap / 2) {
        return row;
      }
      offset += rowHeights[row] + gap;
    }
    return rowHeights.length;
  }

  private parsePixels(value: string, fallback: number): number {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private reorderWidget(widgetId: string, targetIndex: number): void {
    const sourceIndex = this.widgets.findIndex(widget => widget.id === widgetId);
    if (sourceIndex < 0) {
      return;
    }

    const [widget] = this.widgets.splice(sourceIndex, 1);
    const insertIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
    this.widgets.splice(Math.max(0, Math.min(insertIndex, this.widgets.length)), 0, widget);
    this.reflowLayout();
    this.saveLayout();
  }

  private shiftWidgetHorizontally(widget: DashboardWidgetInstance, direction: -1 | 1): boolean {
    const nextX = widget.x + direction;
    if (this.canPlace(widget, nextX, widget.y)) {
      widget.x = nextX;
      return true;
    }

    const target = this.findSwapTarget(widget, direction);
    if (!target) {
      return this.pushBlockersToNextRows(widget, { x: nextX, y: widget.y });
    }

    const swapPosition = this.findSwapPosition(widget, { x: nextX, y: widget.y }, target);
    if (!swapPosition) {
      return this.pushBlockersToNextRows(widget, { x: nextX, y: widget.y });
    }

    const sourceX = widget.x;
    const sourceY = widget.y;
    widget.x = swapPosition.x;
    widget.y = swapPosition.y;
    target.x = sourceX;
    target.y = sourceY;
    return true;
  }

  private canPushWidgetHorizontally(widget: DashboardWidgetInstance, direction: -1 | 1): boolean {
    const nextX = widget.x + direction;
    const width = this.normalizeWidth(widget.width);
    return nextX >= 0
      && nextX + width <= this.gridColumns
      && this.widgets.some(item =>
        item.id !== widget.id
        && item.y === widget.y
        && nextX < item.x + this.normalizeWidth(item.width)
        && nextX + width > item.x
      );
  }

  private pushBlockersToNextRows(
    widget: DashboardWidgetInstance,
    position: DashboardDropPosition
  ): boolean {
    const width = this.normalizeWidth(widget.width);
    if (position.x < 0 || position.x + width > this.gridColumns) {
      return false;
    }

    const blockers = this.widgets.filter(item =>
      item.id !== widget.id
      && item.y === position.y
      && position.x < item.x + this.normalizeWidth(item.width)
      && position.x + width > item.x
    );
    if (blockers.length === 0) {
      return false;
    }

    const placed = this.widgets
      .filter(item => item.id !== widget.id && !blockers.some(blocker => blocker.id === item.id))
      .concat({ ...widget, x: position.x, y: position.y });
    const startRow = Math.max(...this.widgets.map(item => item.y), 0) + 1;

    blockers.forEach(blocker => {
      const nextPosition = this.findFirstAvailablePosition(blocker.width, placed, startRow);
      blocker.x = nextPosition.x;
      blocker.y = nextPosition.y;
      placed.push(blocker);
    });

    widget.x = position.x;
    widget.y = position.y;
    return true;
  }

  private findSwapTarget(
    widget: DashboardWidgetInstance,
    direction: -1 | 1
  ): DashboardWidgetInstance | null {
    const nextX = widget.x + direction;
    const width = this.normalizeWidth(widget.width);
    const candidates = this.widgets
      .filter(item => item.id !== widget.id && item.y === widget.y)
      .filter(item => nextX < item.x + this.normalizeWidth(item.width) && nextX + width > item.x)
      .sort((left, right) => direction === 1 ? left.x - right.x : right.x - left.x);

    return candidates.find(candidate =>
      this.findSwapPosition(widget, { x: nextX, y: widget.y }, candidate) !== null
    ) ?? null;
  }

  private tryPlaceWidget(widget: DashboardWidgetInstance, position: DashboardDropPosition): boolean {
    if (this.canPlace(widget, position.x, position.y)) {
      widget.x = position.x;
      widget.y = position.y;
      return true;
    }

    const blockers = this.widgets.filter(item =>
      item.id !== widget.id
      && item.y === position.y
      && position.x < item.x + this.normalizeWidth(item.width)
      && position.x + this.normalizeWidth(widget.width) > item.x
    );
    if (blockers.length !== 1) {
      return false;
    }

    const blocker = blockers[0];
    const swapPosition = this.findSwapPosition(widget, position, blocker);
    if (!swapPosition) {
      return false;
    }

    const sourceX = widget.x;
    const sourceY = widget.y;
    widget.x = swapPosition.x;
    widget.y = swapPosition.y;
    blocker.x = sourceX;
    blocker.y = sourceY;
    return true;
  }

  private resolveDropPosition(
    widget: DashboardWidgetInstance,
    position: DashboardDropPosition
  ): DashboardDropPosition {
    if (this.canPlace(widget, position.x, position.y)) {
      return position;
    }

    const blockers = this.widgets.filter(item =>
      item.id !== widget.id
      && item.y === position.y
      && position.x < item.x + this.normalizeWidth(item.width)
      && position.x + this.normalizeWidth(widget.width) > item.x
    );
    if (blockers.length !== 1) {
      return position;
    }

    const blocker = blockers[0];
    return this.findSwapPosition(widget, position, blocker) ?? position;
  }

  private findSwapPosition(
    widget: DashboardWidgetInstance,
    position: DashboardDropPosition,
    blocker: DashboardWidgetInstance
  ): DashboardDropPosition | null {
    const others = this.widgets.filter(item => item.id !== widget.id && item.id !== blocker.id);
    const blockerAtSource = { ...blocker, x: widget.x, y: widget.y };

    if (!this.canFit(this.normalizeWidth(blocker.width), widget.x, widget.y, others)) {
      return null;
    }

    const maxX = this.gridColumns - this.normalizeWidth(widget.width);
    const direction = position.x < widget.x ? -1 : 1;
    const candidateX: number[] = [];
    if (direction === 1) {
      for (let x = Math.max(0, position.x); x <= maxX; x += 1) {
        candidateX.push(x);
      }
    } else {
      for (let x = Math.min(maxX, position.x); x >= 0; x -= 1) {
        candidateX.push(x);
      }
    }

    const validX = candidateX.find(x =>
      this.canFit(this.normalizeWidth(widget.width), x, position.y, [...others, blockerAtSource])
    );
    return validX === undefined ? null : { x: validX, y: position.y };
  }

  private reflowLayout(): void {
    const placed: DashboardWidgetInstance[] = [];
    this.widgets.forEach(widget => {
      const position = this.findFirstAvailablePosition(widget.width, placed);
      widget.x = position.x;
      widget.y = position.y;
      placed.push(widget);
    });
  }

  private compactEmptyRows(): void {
    const occupiedRows = [...new Set(this.widgets.map(widget => widget.y))].sort((left, right) => left - right);
    const rowMap = new Map(occupiedRows.map((row, index) => [row, index]));

    this.widgets.forEach(widget => {
      widget.y = rowMap.get(widget.y) ?? widget.y;
    });
  }

  private findFirstAvailablePosition(
    width: number,
    placed: ReadonlyArray<DashboardWidgetInstance>,
    startRow = 0
  ): DashboardDropPosition {
    const normalizedWidth = this.normalizeWidth(width);
    const maxRow = placed.reduce((highest, widget) => Math.max(highest, widget.y), 0) + placed.length + 1;

    for (let y = startRow; y <= maxRow; y += 1) {
      for (let x = 0; x <= this.gridColumns - normalizedWidth; x += 1) {
        if (this.canFit(normalizedWidth, x, y, placed)) {
          return { x, y };
        }
      }
    }

    return { x: 0, y: maxRow + 1 };
  }

  private canPlace(widget: DashboardWidgetInstance, x: number, y: number): boolean {
    return this.canFit(
      this.normalizeWidth(widget.width),
      x,
      y,
      this.widgets.filter(item => item.id !== widget.id)
    );
  }

  private canFit(
    width: number,
    x: number,
    y: number,
    placed: ReadonlyArray<DashboardWidgetInstance>
  ): boolean {
    if (x < 0 || y < 0 || x + width > this.gridColumns) {
      return false;
    }

    return !placed.some(widget =>
      widget.y === y && x < widget.x + this.normalizeWidth(widget.width) && x + width > widget.x
    );
  }

  private normalizeWidth(width: number): number {
    return Math.max(1, Math.min(this.gridColumns, width || 1));
  }

  private createDefaultLayout(): DashboardWidgetInstance[] {
    return this.availableWidgets
      .map(registration => this.createInstance(registration.type, registration.width))
      .filter((item): item is DashboardWidgetInstance => item !== null);
  }

  private createInstance(type: string, width: number): DashboardWidgetInstance | null {
    const registration = this.registry.get(type);
    if (!registration) {
      return null;
    }
    return {
      id: this.createId(),
      type,
      x: 0,
      y: 0,
      width,
      height: registration.height,
      closeable: registration.closeable,
      settings: { ...(registration.defaultSettings ?? {}) }
    };
  }

  private createId(): string {
    return `widget-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
  }
}
